import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, FormField, Input, useToast } from "@/ui";
import { ApiError, confirmPasswordReset } from "@/api";
import { AuthShell } from "@/auth/AuthShell";
import { AuthAlert } from "@/auth/AuthAlert";
import { CheckBigIcon, ChevronBackIcon, CheckIcon, DotIcon, EyeIcon, EyeOffIcon } from "@/auth/icons";
import { useT } from "./i18n";

/**
 * ResetPasswordPage — the link-target that CONSUMES the one-time token (from the e-mail's
 * `?token=…`) and sets a new password via the REAL public endpoint POST /auth/password/reset
 * (iam, publicRoute + authLimiter). The token never leaves the URL → request body; we never display
 * it. Client-side we enforce the GOVERNED password floor (length + character classes) as fast
 * keyboard feedback — the server re-validates and is authoritative. A missing token short-circuits
 * to an invalid-link state; an expired/used token returns a typed failure surfaced the same way.
 */

// Mirror of the server's governed floor (server/src/config/auth.config.js defaults). The server is
// authoritative; these drive only the inline rule checklist + the submit-enable gate.
const PW_MIN_LEN = 8;
const PW_MIN_CLASSES = 2;

/** Count distinct character classes — identical logic to the server's passwordClasses(). */
function passwordClasses(pw: string): number {
  let n = 0;
  if (/[a-z]/.test(pw)) n += 1;
  if (/[A-Z]/.test(pw)) n += 1;
  if (/[0-9]/.test(pw)) n += 1;
  if (/[^A-Za-z0-9]/.test(pw)) n += 1;
  return n;
}

export function ResetPasswordPage() {
  const { t } = useT();
  const toast = useToast();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checks = useMemo(() => {
    const lenOk = pw.length >= PW_MIN_LEN;
    const classesOk = passwordClasses(pw) >= PW_MIN_CLASSES;
    const matchOk = pw.length > 0 && pw === confirm;
    return { lenOk, classesOk, matchOk, all: lenOk && classesOk && matchOk };
  }, [pw, confirm]);

  // No token in the link → cannot proceed. A clear dead-end with a path to request a fresh link.
  if (!token) {
    return (
      <AuthShell>
        <h1 className="nv-auth__h">{t("auth.reset.invalidlink.title")}</h1>
        <p className="nv-auth__sub">{t("auth.reset.subtitle")}</p>
        <AuthAlert tone="red">{t("auth.reset.invalidlink")}</AuthAlert>
        <Link
          to="/mot-de-passe-oublie"
          className="nv-auth-btn primary block"
          style={{ marginBlockStart: "var(--nv-s-4)", textDecoration: "none" }}
        >
          {t("auth.reset.requestnew")}
        </Link>
        <Link to="/login" className="nv-auth__back">
          <ChevronBackIcon />
          <span>{t("auth.reset.tologin")}</span>
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div className="nv-auth__confirm">
          <span className="nv-auth__confirm-badge green" aria-hidden>
            <CheckBigIcon />
          </span>
          <h1 className="nv-auth__h">{t("auth.reset.done.title")}</h1>
          <p className="nv-auth__confirm-text" role="status">
            {t("auth.reset.done")}
          </p>
        </div>
        <Link
          to="/login"
          className="nv-auth-btn primary block"
          style={{ marginBlockStart: "var(--nv-s-4)", textDecoration: "none" }}
        >
          {t("auth.reset.tologin")}
        </Link>
      </AuthShell>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!checks.all || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset({ token, new_password: pw });
      setDone(true);
    } catch (err) {
      const message =
        err instanceof ApiError && err.httpStatus === 429
          ? t("auth.reset.ratelimited")
          : t("auth.reset.error");
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="nv-auth__h">{t("auth.reset.title")}</h1>
      <p className="nv-auth__sub">{t("auth.reset.subtitle")}</p>

      {error && <AuthAlert tone="red">{error}</AuthAlert>}

      <form onSubmit={onSubmit} className="nv-auth__form" style={{ marginBlockStart: "var(--nv-s-4)" }}>
        <FormField label={t("auth.reset.password")} required>
          {(p) => (
            <span className="nv-auth__pw">
              <Input
                {...p}
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth.reset.password.placeholder")}
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
              />
              <button
                type="button"
                className="nv-auth__pw-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? t("auth.login.pw.hide") : t("auth.login.pw.show")}
                aria-pressed={showPw}
              >
                {showPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </span>
          )}
        </FormField>

        <FormField
          label={t("auth.reset.confirm")}
          required
          {...(confirm.length > 0 && !checks.matchOk ? { error: t("auth.reset.rule.match") } : {})}
        >
          {(p) => (
            <Input
              {...p}
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          )}
        </FormField>

        <ul className="nv-auth__rules" aria-label={t("auth.reset.subtitle")}>
          <Rule ok={checks.lenOk}>{t("auth.reset.rule.length", { n: PW_MIN_LEN })}</Rule>
          <Rule ok={checks.classesOk}>{t("auth.reset.rule.classes", { n: PW_MIN_CLASSES })}</Rule>
          <Rule ok={checks.matchOk}>{t("auth.reset.rule.match")}</Rule>
        </ul>

        <Button type="submit" block loading={submitting} disabled={!checks.all}>
          {submitting ? t("auth.reset.submitting") : t("auth.reset.submit")}
        </Button>
      </form>

      <Link to="/login" className="nv-auth__back">
        <ChevronBackIcon />
        <span>{t("auth.reset.tologin")}</span>
      </Link>
    </AuthShell>
  );
}

function Rule({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <li className={`nv-auth__rule${ok ? " is-ok" : ""}`}>
      {ok ? <CheckIcon width={14} height={14} /> : <DotIcon width={14} height={14} />}
      <span>{children}</span>
    </li>
  );
}
